// Global number formatting utilities with Portuguese locale as default
// Centralized locale configuration for consistent number formatting across the application

/**
 * Default locale configuration for the application
 * Portuguese (Brazil): 1.234.567,89 format (dot for thousands, comma for decimals)
 */
const DEFAULT_LOCALE = "pt-BR";

/**
 * Get the current locale for number formatting
 * Can be overridden via environment variable or settings in the future
 * @returns {string} - BCP 47 language tag (e.g., 'pt-BR', 'en-US')
 */
export const getFormattingLocale = () => {
  // Future: Could read from user settings, environment variables, or browser preferences
  // For now, always return Portuguese (Brazil) as specified
  return DEFAULT_LOCALE;
};

/**
 * Cached Intl.NumberFormat instances — avoids recreating on every call.
 * Key: "locale:decimalPlaces" → Intl.NumberFormat
 */
const _formatters = new Map();
function _getFormatter(decimalPlaces, locale) {
  const key = `${locale}:${decimalPlaces}`;
  let fmt = _formatters.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
    _formatters.set(key, fmt);
  }
  return fmt;
}

/**
 * Format a number with appropriate decimal places and locale-specific separators
 * @param {number} value - The number to format
 * @param {number} decimalPlaces - Number of decimal places (default: 2)
 * @param {string} locale - BCP 47 locale code (default: from getFormattingLocale())
 * @returns {string} - Formatted number string
 */
export const formatNumber = (
  value,
  decimalPlaces = 2,
  locale = getFormattingLocale(),
) => {
  if (!isFinite(value)) return "0" + (decimalPlaces > 0 ? ",00" : "");

  return _getFormatter(decimalPlaces, locale).format(value);
};

/**
 * Format a price/financial value with locale-specific formatting
 * @param {number} value - The price to format
 * @param {number} decimalPlaces - Number of decimal places (default: 2)
 * @returns {string} - Formatted price string
 */
export const formatPrice = (value, decimalPlaces = 2) => {
  return formatNumber(value, decimalPlaces);
};

/**
 * Format a volume (integer) with locale-specific thousand separators
 * @param {number} value - The volume to format
 * @returns {string} - Formatted volume string
 */
export const formatVolume = (value) => {
  return formatNumber(value, 0);
};

/**
 * Format a volume with K/M/B abbreviations for compact display
 * Supports negative values (e.g., Force Index indicator)
 * @param {number} value - The volume to format
 * @param {number} decimalPlaces - Decimal places for abbreviated values (default: 2)
 * @returns {string} - Formatted volume string (e.g., "1,23M", "456K", "789")
 */
export const formatVolumeAbbrev = (value, decimalPlaces = 2) => {
  if (!isFinite(value)) return "0";
  const absVal = Math.abs(value);
  if (absVal >= 1e9) return formatNumber(value / 1e9, decimalPlaces) + "B";
  if (absVal >= 1e6) return formatNumber(value / 1e6, decimalPlaces) + "M";
  if (absVal >= 1e3) return formatNumber(value / 1e3, decimalPlaces) + "K";
  return formatNumber(value, 0);
};

/**
 * Format a percentage with locale-specific formatting
 * @param {number} value - The percentage value (as decimal, e.g., 0.1234 for 12.34%)
 * @param {number} decimalPlaces - Number of decimal places (default: 2)
 * @returns {string} - Formatted percentage string
 */
export const formatPercentage = (value, decimalPlaces = 2) => {
  if (!isFinite(value)) return "0,00%";

  return (
    _getFormatter(decimalPlaces, getFormattingLocale()).format(value * 100) +
    "%"
  );
};

/**
 * Format a ratio or multiplier value (e.g., 1.25x, 0.85x)
 * @param {number} value - The ratio to format
 * @param {number} decimalPlaces - Number of decimal places (default: 2)
 * @returns {string} - Formatted ratio string
 */
export const formatRatio = (value, decimalPlaces = 2) => {
  return formatNumber(value, decimalPlaces);
};

/**
 * Check if a value should be hidden (is n/a, null, undefined, or NaN)
 * @param {any} value - The value to check
 * @param {string} naText - The "not available" text to compare against (default: "n/a")
 * @returns {boolean} - True if the field should be hidden
 */
export const shouldHideField = (value, naText = "n/a") => {
  return (
    value === naText ||
    value === null ||
    value === undefined ||
    (typeof value === "number" && !isFinite(value))
  );
};

/**
 * Helper to safely extract numeric value from various data formats
 * Handles both direct numbers and objects with nested properties
 * @param {any} data - The data to extract from
 * @param {string} property - Property name to extract (optional)
 * @returns {number|null} - Extracted numeric value or null
 */
export const extractNumericValue = (data, property = null) => {
  if (property && data && typeof data === "object") {
    return typeof data[property] === "number" ? data[property] : null;
  }

  if (typeof data === "number") {
    return data;
  }

  return null;
};

/**
 * Create a conditional JSX element that only renders if the value is not n/a
 * @param {any} value - The value to check
 * @param {function} renderFn - Function that returns JSX to render
 * @param {string} naText - The "not available" text (default: "n/a")
 * @returns {JSX.Element|null} - JSX element or null
 */
export const renderIfNotNA = (value, renderFn, naText = "n/a") => {
  return shouldHideField(value, naText) ? null : renderFn(value);
};
